import React, { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, TextField, Avatar, CircularProgress,
    IconButton, Skeleton, Chip, Button, Collapse, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, InputAdornment,
    Fab, Autocomplete, Snackbar, Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddCommentIcon from '@mui/icons-material/AddComment';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import SendAndArchiveIcon from '@mui/icons-material/SendAndArchive';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es';

import {
    fetchAllConversationsFromAPI,
    sendMessageToConversation,
    updateConversationNoteInAPI,
    pollConversationMessages,
    closeConversationInAPI,
    takeoverConversationInAPI,
    startConversationInAPI,
    sendTemplateToConversationInAPI,
    reactivateConversationInAPI,
    markConversationRead,
} from '../../Redux/ConversationDucks';
import { enqueueConversationInAPI } from '../../Redux/WaitingQueueDucks';
import { fetchBusinessesFromAPI } from '../../Redux/BusinessDucks';
import { fetchAccountsFromAPI } from '../../Redux/AccountDucks';
import { GetConversationMessagesOnly } from '../../Redux/MessageDucks';
import WhatsAppChat from '../../components/MessagingView/ChatInterface';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

// Zona horaria fija de la operación (Chile). Evita depender de la config del PC del agente.
const APP_TZ = 'America/Santiago';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getToken = () => new Cookies().get('token');

const getUserBusinessLineIds = () => {
    const cookies = document.cookie.split('; ');
    const perfilCookie = cookies.find(c => c.startsWith('perfil='));
    const perfil = perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) : '';
    if (perfil === 'Super-Admin') return null;
    const blCookie = cookies.find(c => c.startsWith('businessLineIds='));
    if (!blCookie) return [];
    return blCookie.split('=')[1].split(',').map(Number).filter(Boolean);
};

const fmtPhone = (tel) => {
    if (!tel) return '';
    const s = String(tel);
    return s.length > 4 ? `+(${s.substring(0, 2)}) ${s.substring(2)}` : s;
};

const fmtTime = (dateStr) => dayjs.utc(dateStr).tz(APP_TZ).format('DD/MM HH:mm');

const getInitials = (conv) => {
    const name = conv?.clientWSP?.fullName || conv?.clientWSP?.name;
    if (name?.trim()) {
        return name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
    }
    return (conv?.clientWSP?.whatsAppId || '??').slice(-2);
};

const getDisplayName = (conv) =>
    conv?.clientWSP?.fullName || conv?.clientWSP?.name || fmtPhone(conv?.clientWSP?.whatsAppId);

// Meta: ventana de 24h desde el último mensaje del cliente
const isWindowExpired = (conv) => {
    const last = conv?.lastClientMessageAt;
    if (!last) return true; // sin registro = asumir expirada para ser conservador
    return dayjs().diff(dayjs.utc(last), 'hour') >= 24;
};

const windowTimeLeft = (conv) => {
    const last = conv?.lastClientMessageAt;
    if (!last) return null;
    const expiresAt = dayjs.utc(last).add(24, 'hour');
    const diff = expiresAt.diff(dayjs(), 'minute');
    if (diff <= 0) return null;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const CONVERSATION_STATUS_CONFIG = {
    en_cola: { color: '#F59E0B', label: 'Esperando agente' },
    sin_respuesta: { color: '#EF4444', label: 'Sin respuesta +30m' },
    bot_activo: { color: '#3B82F6', label: 'Bot activo' },
    cerrada: { color: '#6B7280', label: 'Cerrada' },
};

const getConversationRawStatus = (conv) =>
    String(conv?.status || conv?.conversationStatus || conv?.state || conv?.State || '').toLowerCase();

const isConversationQueued = (conv) => {
    const rawStatus = getConversationRawStatus(conv);
    return Boolean(
        conv?.inQueue ||
        conv?.isInQueue ||
        conv?.queued ||
        conv?.queueEntryId ||
        conv?.queueStatus ||
        rawStatus.includes('en_cola') ||
        rawStatus.includes('queue') ||
        rawStatus.includes('espera')
    );
};

const getConversationStatusKey = (conv) => {
    const rawStatus = getConversationRawStatus(conv);
    const isActive = (conv?.active ?? conv?.Active) === true;
    const unreadCount = Number(conv?.unreadCount ?? conv?.unread_count ?? 0);
    const lastClientAt = conv?.lastClientMessageAt || conv?.lastInboundMessageAt || conv?.lastMessageAt;

    if (rawStatus.includes('en_cola') || rawStatus.includes('queue') || rawStatus.includes('espera')) return 'en_cola';
    if (rawStatus.includes('sin_respuesta') || rawStatus.includes('unanswered') || rawStatus.includes('no_response')) return 'sin_respuesta';
    if (rawStatus.includes('cerrada') || rawStatus.includes('closed')) return 'cerrada';
    if (rawStatus.includes('bot_activo') || rawStatus.includes('bot_active')) return 'bot_activo';
    if (!isActive) return 'cerrada';
    if (isConversationQueued(conv)) return 'en_cola';
    if (unreadCount > 0 && lastClientAt && dayjs().diff(dayjs.utc(lastClientAt), 'minute') >= 30) return 'sin_respuesta';
    return 'bot_activo';
};

const getConversationStatusMeta = (conv) =>
    CONVERSATION_STATUS_CONFIG[getConversationStatusKey(conv)] || CONVERSATION_STATUS_CONFIG.bot_activo;

const truncateText = (text, max = 60) => {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    return normalized.length > max ? `${normalized.slice(0, max - 1)}…` : normalized;
};

const getConversationPreview = (conv) => {
    const candidates = [
        conv?.lastMessagePreview,
        conv?.lastMessageText,
        conv?.lastMessage,
        conv?.lastClientMessage,
        conv?.stageSummary,
        conv?.StageSummary,
        conv?.summary,
    ];
    const firstText = candidates.find(value => typeof value === 'string' && value.trim());
    if (firstText) return truncateText(firstText, 60);
    return truncateText(fmtPhone(conv?.clientWSP?.whatsAppId), 60);
};

// ── ConversationItem ──────────────────────────────────────────────────────────

const ConversationItem = ({ conv, isSelected, onClick }) => {
    const isActive = conv?.active;
    const isHumanControl = Boolean(conv?.humanControl);
    const initials = getInitials(conv);
    const name = getDisplayName(conv);
    const timestamp = conv?.startedAt ? fmtTime(conv.startedAt) : '';
    const unreadCount = Number(conv?.unreadCount ?? conv?.unread_count ?? 0);
    const statusMeta = getConversationStatusMeta(conv);
    const previewText = getConversationPreview(conv);

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.5, cursor: 'pointer',
                borderLeft: `4px solid ${statusMeta.color}`,
                borderBottom: '1px solid #eef2f7',
                bgcolor: isSelected ? '#f0f7ff' : 'transparent',
                '&:hover': { bgcolor: isSelected ? '#f0f7ff' : '#f5f5f5' },
                transition: 'background 0.15s',
            }}
        >
            <Box sx={{ position: 'relative', flexShrink: 0, alignSelf: 'center' }}>
                <Avatar sx={{
                    bgcolor: isHumanControl ? '#7c3aed' : (isSelected ? '#007AFF' : '#5b8def'),
                    width: 42, height: 42, fontSize: 14,
                }}>
                    {initials}
                </Avatar>
                <Box sx={{
                    position: 'absolute', bottom: 1, right: 1,
                    width: 10, height: 10,
                    borderRadius: '50%',
                    bgcolor: isActive ? '#22c55e' : '#9e9e9e',
                    border: '2px solid #fff',
                }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.45 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={unreadCount > 0 ? 700 : 500} noWrap sx={{ flex: 1, minWidth: 0 }}>
                        {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ flexShrink: 0 }}>
                        {timestamp}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{
                            color: 'text.secondary',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {previewText}
                    </Typography>
                    {unreadCount > 0 && (
                        <Box sx={{
                            bgcolor: '#007AFF', color: '#fff',
                            borderRadius: 999, minWidth: 24, height: 20,
                            fontSize: 11, fontWeight: 700, px: 0.75,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ── ConversationHeader ────────────────────────────────────────────────────────

const ConversationHeader = ({
    conversation, humanControl, notesOpen,
    onToggleNotes, onClose, closing,
    onTakeover, takingOver,
    onReactivate, reactivating,
    onEnqueue, enqueuing, enqueueSuccess,
}) => {
    const initials = getInitials(conversation);
    const name = getDisplayName(conversation);
    const phone = fmtPhone(conversation?.clientWSP?.whatsAppId);
    const hasName = !!(conversation?.clientWSP?.fullName || conversation?.clientWSP?.name);
    const isActive = conversation?.active;
    const startedAt = conversation?.startedAt ? fmtTime(conversation.startedAt) : '—';
    const bl = conversation?.accountWSP?.businessLine;
    const blLabel = bl?.name || bl?.siglas;

    const subtitleParts = [hasName ? phone : null, blLabel, `Iniciada ${startedAt}`].filter(Boolean).join(' · ');

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2, py: 1,
            borderBottom: '1px solid #e8e8e8',
            bgcolor: '#fff', flexShrink: 0,
        }}>
            <Avatar sx={{
                bgcolor: humanControl ? '#7c3aed' : '#007AFF',
                width: 36, height: 36, fontSize: 13, flexShrink: 0,
            }}>
                {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} noWrap>{name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                    {subtitleParts}
                </Typography>
            </Box>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
                {/* Tomar / Liberar control */}
                {isActive && (
                    <Tooltip title={humanControl ? 'Liberar control (reactivar bot)' : 'Tomar control (pausar bot)'}>
                        <Button
                            size="small"
                            variant={humanControl ? 'contained' : 'outlined'}
                            startIcon={
                                takingOver
                                    ? <CircularProgress size={12} sx={{ color: humanControl ? '#fff' : '#7c3aed' }} />
                                    : humanControl
                                        ? <SmartToyIcon sx={{ fontSize: 14 }} />
                                        : <SupportAgentIcon sx={{ fontSize: 14 }} />
                            }
                            onClick={onTakeover}
                            disabled={takingOver}
                            sx={{
                                fontSize: 11, textTransform: 'none', fontWeight: 600,
                                borderRadius: 2, px: 1.25, py: 0.4, height: 28,
                                ...(humanControl
                                    ? { bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, color: '#fff', borderColor: '#7c3aed' }
                                    : { borderColor: '#7c3aed', color: '#7c3aed', '&:hover': { bgcolor: '#f5f3ff' } }
                                ),
                            }}
                        >
                            {humanControl ? 'Liberar bot' : 'Tomar control'}
                        </Button>
                    </Tooltip>
                )}

                {/* Poner en cola */}
                {isActive && (
                    <Tooltip title={enqueueSuccess ? '¡En cola!' : 'Poner en cola de espera'}>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                                enqueuing
                                    ? <CircularProgress size={12} sx={{ color: '#7c3aed' }} />
                                    : <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                            }
                            onClick={onEnqueue}
                            disabled={enqueuing}
                            sx={{
                                fontSize: 11, textTransform: 'none', fontWeight: 600,
                                borderRadius: 2, px: 1.25, py: 0.4, height: 28,
                                borderColor: enqueueSuccess ? '#22c55e' : '#d1d5db',
                                color: enqueueSuccess ? '#16a34a' : '#6b7280',
                                bgcolor: enqueueSuccess ? '#f0fdf4' : 'transparent',
                                '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                                transition: 'all 0.25s',
                            }}
                        >
                            {enqueueSuccess ? '¡En cola!' : 'Poner en cola'}
                        </Button>
                    </Tooltip>
                )}

                <Tooltip title="Notas internas">
                    <IconButton
                        size="small"
                        onClick={onToggleNotes}
                        sx={{
                            color: notesOpen ? '#007AFF' : '#9e9e9e',
                            bgcolor: notesOpen ? '#e8f0fe' : 'transparent',
                        }}
                    >
                        <StickyNote2Icon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {isActive ? (
                    <Tooltip title="Cerrar conversación">
                        <span>
                            <IconButton
                                size="small"
                                onClick={onClose}
                                disabled={closing}
                                sx={{ color: closing ? '#ccc' : '#d32f2f' }}
                            >
                                {closing
                                    ? <CircularProgress size={16} sx={{ color: '#d32f2f' }} />
                                    : <CheckCircleOutlineIcon fontSize="small" />
                                }
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : (
                    <Tooltip title="Reactivar conversación">
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={
                                reactivating
                                    ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                                    : <LockOpenIcon sx={{ fontSize: 14 }} />
                            }
                            onClick={onReactivate}
                            disabled={reactivating}
                            sx={{
                                fontSize: 11, textTransform: 'none', fontWeight: 600,
                                borderRadius: 2, px: 1.25, py: 0.4, height: 28,
                                bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, color: '#fff',
                            }}
                        >
                            {reactivating ? 'Reactivando...' : 'Reactivar'}
                        </Button>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

// ── TemplatePickerModal ───────────────────────────────────────────────────────

const TemplatePickerModal = ({ open, onClose, onSend, accountId }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open || !accountId) return;
        setLoading(true);
        axios.get(`${window.API_BASE_URL}/Templates/GetSimple/${accountId}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
        })
            .then(r => setTemplates(Array.isArray(r.data) ? r.data : []))
            .catch(() => setTemplates([]))
            .finally(() => setLoading(false));
    }, [open, accountId]);

    const handleSend = async () => {
        if (!selected) return;
        setSending(true);
        try { await onSend(selected); }
        finally { setSending(false); }
    };

    const handleClose = () => {
        if (sending) return;
        setSelected('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SendAndArchiveIcon sx={{ color: '#c2410c' }} />
                    Enviar plantilla
                </Box>
                <Typography variant="caption" color="text.secondary" fontWeight={400}>
                    La ventana de 24h expiró. Solo puedes recontactar con una plantilla aprobada.
                </Typography>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : templates.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                        No hay plantillas aprobadas para esta cuenta.
                    </Typography>
                ) : (
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel>Plantilla</InputLabel>
                        <Select value={selected} label="Plantilla" onChange={e => setSelected(e.target.value)}>
                            {templates.map(t => (
                                <MenuItem key={t.id} value={t.id}>
                                    <Typography variant="body2">{t.name}</Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={handleClose} disabled={sending} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSend}
                    disabled={sending || !selected}
                    startIcon={sending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendAndArchiveIcon />}
                    sx={{
                        textTransform: 'none', fontWeight: 600, borderRadius: 2,
                        bgcolor: '#c2410c', '&:hover': { bgcolor: '#9a3412' },
                    }}
                >
                    {sending ? 'Enviando...' : 'Enviar plantilla'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── NewConversationModal ──────────────────────────────────────────────────────

const NewConversationModal = ({ open, onClose, onStart, accounts, preContact, restrictToCartera }) => {
    const [search, setSearch] = useState('');
    const [contacts, setContacts] = useState([]);
    const [carteraContacts, setCarteraContacts] = useState([]); // cartera del consultor (solo si restrictToCartera)
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [selectedContact, setSelectedContact] = useState(preContact || null);
    const [selectedAccount, setSelectedAccount] = useState('');
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [starting, setStarting] = useState(false);

    // Pre-fill contact if provided
    useEffect(() => {
        if (preContact) setSelectedContact(preContact);
    }, [preContact]);

    // Consultor: la cartera es la única fuente de contactos. Se carga al abrir y se filtra localmente.
    useEffect(() => {
        if (!open || !restrictToCartera) { setCarteraContacts([]); return; }
        let cancel = false;
        setLoadingContacts(true);
        axios.get(`${window.API_BASE_URL}/assignments/cartera`, {
            headers: { Authorization: `Bearer ${getToken()}` },
        })
            .then(r => {
                if (cancel) return;
                const raw = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                // Mapear DTO de cartera al shape de contacto del modal
                setCarteraContacts(raw.map(a => ({
                    id: a.clientId,
                    fullName: a.clientName,
                    whatsAppId: a.clientPhone,
                })));
            })
            .catch(() => { if (!cancel) setCarteraContacts([]); })
            .finally(() => { if (!cancel) setLoadingContacts(false); });
        return () => { cancel = true; };
    }, [open, restrictToCartera]);

    // Cargar plantillas aprobadas de la cuenta seleccionada (primer contacto requiere plantilla)
    useEffect(() => {
        setSelectedTemplate('');
        if (!selectedAccount) { setTemplates([]); return; }
        setLoadingTemplates(true);
        axios.get(`${window.API_BASE_URL}/Templates/GetSimple/${selectedAccount}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
        })
            .then(r => setTemplates(Array.isArray(r.data) ? r.data : []))
            .catch(() => setTemplates([]))
            .finally(() => setLoadingTemplates(false));
    }, [selectedAccount]);

    // Búsqueda de contactos.
    // - Consultor (restrictToCartera): filtra localmente sobre su cartera ya cargada.
    // - Supervisor/Super-Admin: busca en todos los clientes vía API.
    useEffect(() => {
        if (restrictToCartera) {
            const q = search.trim().toLowerCase();
            const base = q
                ? carteraContacts.filter(c =>
                    (c.fullName || '').toLowerCase().includes(q) ||
                    (c.whatsAppId || '').includes(q))
                : carteraContacts;
            setContacts(base.slice(0, 20));
            return;
        }
        if (!search || search.length < 2) { setContacts([]); return; }
        const t = setTimeout(async () => {
            setLoadingContacts(true);
            try {
                const res = await axios.get(`${window.API_BASE_URL}/Clients`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                    params: { search, page: 1, pageSize: 10 },
                });
                const raw = res.data;
                setContacts(Array.isArray(raw) ? raw : (raw?.items || []));
            } catch { setContacts([]); }
            finally { setLoadingContacts(false); }
        }, 400);
        return () => clearTimeout(t);
    }, [search, restrictToCartera, carteraContacts]);

    const handleStart = async () => {
        if (!selectedContact || !selectedAccount || !selectedTemplate) return;
        setStarting(true);
        try {
            await onStart(selectedContact.id, selectedAccount, undefined, selectedTemplate);
            setSearch(''); setSelectedContact(preContact || null);
            setSelectedAccount(''); setSelectedTemplate('');
        } finally {
            setStarting(false);
        }
    };

    const handleClose = () => {
        if (starting) return;
        setSearch(''); setContacts([]);
        setSelectedContact(preContact || null);
        setSelectedAccount(''); setSelectedTemplate('');
        onClose();
    };

    const activeAccounts = accounts.filter(a => a.active !== false);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddCommentIcon sx={{ color: '#007AFF' }} />
                    Nueva conversación
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1.5 }}>

                {/* Contacto */}
                {selectedContact ? (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        p: 1.5, bgcolor: '#f0f7ff', borderRadius: 2,
                        border: '1px solid #bcd4f7',
                    }}>
                        <Avatar sx={{ bgcolor: '#007AFF', width: 36, height: 36, fontSize: 13 }}>
                            {(selectedContact.fullName || selectedContact.name || '?')
                                .trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('')}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap>
                                {selectedContact.fullName || selectedContact.name || fmtPhone(selectedContact.whatsAppId)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {fmtPhone(selectedContact.whatsAppId)}
                            </Typography>
                        </Box>
                        {!preContact && (
                            <IconButton size="small" onClick={() => setSelectedContact(null)}>
                                <Typography sx={{ fontSize: 16, color: '#999' }}>×</Typography>
                            </IconButton>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <TextField
                            fullWidth size="small"
                            label={restrictToCartera ? 'Buscar en tu cartera' : 'Buscar contacto'}
                            placeholder={restrictToCartera ? 'Nombre o teléfono de tu cartera...' : 'Nombre, teléfono o RUT...'}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <PersonSearchIcon sx={{ fontSize: 18, color: '#999' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: loadingContacts && (
                                    <InputAdornment position="end">
                                        <CircularProgress size={14} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {contacts.length > 0 && (
                            <Box sx={{
                                mt: 0.5, border: '1px solid #e0e0e0', borderRadius: 2,
                                maxHeight: 180, overflowY: 'auto', bgcolor: '#fff',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                            }}>
                                {contacts.map(c => (
                                    <Box
                                        key={c.id}
                                        onClick={() => { setSelectedContact(c); setSearch(''); setContacts([]); }}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                            px: 1.5, py: 1, cursor: 'pointer',
                                            '&:hover': { bgcolor: '#f5f5f5' },
                                        }}
                                    >
                                        <Avatar sx={{ bgcolor: '#007AFF', width: 32, height: 32, fontSize: 11 }}>
                                            {(c.fullName || c.name || '?')
                                                .trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('')}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                {c.fullName || c.name || fmtPhone(c.whatsAppId)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {fmtPhone(c.whatsAppId)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Cuenta WSP */}
                <FormControl fullWidth size="small">
                    <InputLabel>Cuenta WhatsApp</InputLabel>
                    <Select
                        value={selectedAccount}
                        label="Cuenta WhatsApp"
                        onChange={e => setSelectedAccount(e.target.value)}
                    >
                        {activeAccounts.map(a => (
                            <MenuItem key={a.id} value={a.id}>
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                        {fmtPhone(a.number) || a.waba}
                                    </Typography>
                                    {a.businessLineName && (
                                        <Typography variant="caption" color="text.secondary">
                                            {a.businessLineName}
                                        </Typography>
                                    )}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Plantilla de primer contacto (obligatoria: WhatsApp exige plantilla aprobada
                    para iniciar una conversación con el cliente) */}
                <FormControl fullWidth size="small" disabled={!selectedAccount || loadingTemplates}>
                    <InputLabel>Plantilla de contacto</InputLabel>
                    <Select
                        value={selectedTemplate}
                        label="Plantilla de contacto"
                        onChange={e => setSelectedTemplate(e.target.value)}
                    >
                        {templates.map(t => (
                            <MenuItem key={t.id} value={t.id}>
                                <Typography variant="body2">{t.name}</Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedAccount && !loadingTemplates && templates.length === 0 && (
                    <Typography variant="caption" color="error" sx={{ mt: -1 }}>
                        No hay plantillas aprobadas para esta cuenta. Crea o aprueba una plantilla antes de contactar.
                    </Typography>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                    Se enviará la plantilla seleccionada al contacto. El agente tomará control automáticamente:
                    el bot no intervendrá hasta que liberes el control.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={handleClose} disabled={starting} sx={{ textTransform: 'none' }}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={handleStart}
                    disabled={starting || !selectedContact || !selectedAccount || !selectedTemplate}
                    startIcon={starting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AddCommentIcon />}
                    sx={{
                        textTransform: 'none', fontWeight: 600, borderRadius: 2,
                        bgcolor: '#007AFF', '&:hover': { bgcolor: '#005ecb' },
                    }}
                >
                    {starting ? 'Iniciando...' : 'Iniciar conversación'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── EmptyState ────────────────────────────────────────────────────────────────

const EmptyState = () => (
    <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', gap: 1.5, color: 'text.secondary',
    }}>
        <Box sx={{ fontSize: 48, opacity: 0.25, lineHeight: 1 }}>💬</Box>
        <Typography variant="body2" color="text.secondary">Selecciona una conversación</Typography>
    </Box>
);

// ── Main page ─────────────────────────────────────────────────────────────────

const ConversacionesPage = () => {
    const dispatch = useDispatch();
    const location = useLocation();

    const userBLIds = getUserBusinessLineIds();
    const defaultBL = (userBLIds !== null && userBLIds.length > 0) ? userBLIds[0] : null;

    const [selectedConversation, setSelectedConversation] = useState(
        location.state?.conversation ?? null
    );
    const [humanControl, setHumanControl] = useState(
        location.state?.conversation?.humanControl ?? false
    );
    const [filterText, setFilterText] = useState('');
    const [messageInput, setMessageInput] = useState('');
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [selectedBusinessLine, setSelectedBusinessLine] = useState(defaultBL);
    const [statusFilter, setStatusFilter] = useState('todas');
    const [agentNote, setAgentNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [noteSaved, setNoteSaved] = useState(false);
    const [closingConversation, setClosingConversation] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    const [takingOver, setTakingOver] = useState(false);
    const [reactivatingConversation, setReactivatingConversation] = useState(false);
    const [newConvOpen, setNewConvOpen] = useState(false);
    const [newConvPreContact, setNewConvPreContact] = useState(null);
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [enqueueSuccess, setEnqueueSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [pendingSelectId, setPendingSelectId] = useState(null);

    const allConversationsList = useSelector(store => store.conversation.allConversationsList);
    const enqueuing = useSelector(store => store.waitingQueue?.enqueuing ?? false);
    const sendingMessage = useSelector(store => store.conversation.sendingMessage);
    const messagesState = useSelector(store => store.message.messages);
    const allBusinessLines = useSelector(store => store.business);
    const accounts = useSelector(store => store.account?.accounts || []);

    const businessLines = userBLIds === null
        ? allBusinessLines
        : (Array.isArray(allBusinessLines) ? allBusinessLines.filter(bl => userBLIds.includes(bl.id)) : []);
    const businessLineOptions = [
        ...(userBLIds === null ? [{ id: null, label: 'Todas' }] : []),
        ...(Array.isArray(businessLines)
            ? businessLines.map(bl => ({
                id: bl.id,
                label: bl.name || bl.nombre || bl.siglas || `BL ${bl.id}`,
            }))
            : []),
    ];
    const selectedBusinessLineOption =
        businessLineOptions.find(option => option.id === selectedBusinessLine) || null;

    useEffect(() => {
        dispatch(fetchBusinessesFromAPI());
        dispatch(fetchAccountsFromAPI());
    }, [dispatch]);

    useEffect(() => {
        setLoadingList(true);
        dispatch(fetchAllConversationsFromAPI(selectedBusinessLine));
    }, [selectedBusinessLine, dispatch]);

    useEffect(() => {
        const id = setInterval(() => {
            dispatch(fetchAllConversationsFromAPI(selectedBusinessLine));
        }, 30_000);
        return () => clearInterval(id);
    }, [selectedBusinessLine, dispatch]);

    useEffect(() => {
        if (!selectedConversation) return;
        const id = setInterval(() => {
            dispatch(pollConversationMessages(selectedConversation.id));
        }, 10_000);
        return () => clearInterval(id);
    }, [selectedConversation, dispatch]);

    useEffect(() => { setLoadingList(false); }, [allConversationsList]);
    useEffect(() => { setLoadingMessages(false); }, [messagesState.list]);

    useEffect(() => {
        if (selectedConversation) {
            setAgentNote(selectedConversation.summary || '');
            setHumanControl(selectedConversation.humanControl ?? false);
            setNoteSaved(false);
            dispatch(GetConversationMessagesOnly(selectedConversation.id));
        }
    }, [selectedConversation]); // eslint-disable-line

    // Handle navigation from ContactosPage with newConversationContact
    useEffect(() => {
        if (location.state?.newConversationContact) {
            setNewConvPreContact(location.state.newConversationContact);
            setNewConvOpen(true);
        }
    }, [location.state]);

    const filteredConversations = allConversationsList.filter(conv =>
        ((conv?.clientWSP?.whatsAppId || '').toLowerCase().includes(filterText.toLowerCase()) ||
        (conv?.clientWSP?.fullName || conv?.clientWSP?.name || '').toLowerCase().includes(filterText.toLowerCase())) &&
        (
            statusFilter === 'todas' ||
            (statusFilter === 'abiertas' && (conv?.active ?? conv?.Active) === true) ||
            (statusFilter === 'en_cola' && getConversationStatusKey(conv) === 'en_cola') ||
            (statusFilter === 'sin_respuesta' && getConversationStatusKey(conv) === 'sin_respuesta') ||
            (statusFilter === 'cerradas' && getConversationStatusKey(conv) === 'cerrada')
        )
    );

    const statusCounts = allConversationsList.reduce((acc, conv) => {
        const statusKey = getConversationStatusKey(conv);
        acc.todas += 1;
        if ((conv?.active ?? conv?.Active) === true) acc.abiertas += 1;
        if (statusKey === 'en_cola') acc.en_cola += 1;
        if (statusKey === 'sin_respuesta') acc.sin_respuesta += 1;
        if (statusKey === 'cerrada') acc.cerradas += 1;
        return acc;
    }, { todas: 0, abiertas: 0, en_cola: 0, sin_respuesta: 0, cerradas: 0 });

    const statusFilters = [
        { key: 'todas', label: 'Todas' },
        { key: 'abiertas', label: 'Abiertas' },
        { key: 'en_cola', label: 'En cola' },
        { key: 'sin_respuesta', label: 'Sin respuesta' },
        { key: 'cerradas', label: 'Cerradas' },
    ];
    const statusFilterOptions = statusFilters.map(filter => ({
        key: filter.key,
        label: `${filter.label} (${statusCounts[filter.key] ?? 0})`,
    }));
    const selectedStatusFilterOption =
        statusFilterOptions.find(option => option.key === statusFilter) || statusFilterOptions[0];

    const selectedConversationStatus = selectedConversation ? getConversationStatusKey(selectedConversation) : null;
    const contextualBarState = selectedConversation ? (() => {
        const isActive = selectedConversation?.active ?? selectedConversation?.Active;
        // Priority 1: Queue
        if (isActive && selectedConversationStatus === 'en_cola') {
            return {
                bg: '#fffbeb', border: '#fde68a', color: '#b45309',
                icon: <AccessTimeIcon sx={{ fontSize: 16, color: '#b45309' }} />,
                text: 'Cliente esperando atención',
            };
        }
        // Priority 2: Window closing soon
        if (isActive && !isWindowExpired(selectedConversation) &&
            windowTimeLeft(selectedConversation) &&
            dayjs().diff(dayjs.utc(selectedConversation.lastClientMessageAt), 'hour') >= 22) {
            return {
                bg: '#fffbeb', border: '#fde68a', color: '#d97706',
                icon: <TimerOffIcon sx={{ fontSize: 16, color: '#d97706' }} />,
                text: `Ventana cierra en ${windowTimeLeft(selectedConversation)} — responde antes de que expire.`,
            };
        }
        // Priority 3: Agent / Bot control status
        if (isActive && humanControl) {
            return {
                bg: '#f5f3ff', border: '#ede9fe', color: '#6d28d9',
                icon: <SupportAgentIcon sx={{ fontSize: 16, color: '#6d28d9' }} />,
                text: 'Agente en control · Bot pausado',
            };
        }
        if (isActive) {
            return {
                bg: '#eff6ff', border: '#dbeafe', color: '#1d4ed8',
                icon: <SmartToyIcon sx={{ fontSize: 16, color: '#1d4ed8' }} />,
                text: 'Bot en control · Conversación automática',
            };
        }
        return null;
    })() : null;

    const handleSelectConversation = (conv) => {
        setSelectedConversation(conv);
        setHumanControl(conv.humanControl ?? false);
        setLoadingMessages(true);
        dispatch(GetConversationMessagesOnly(conv.id));
        if (conv.unreadCount > 0) {
            dispatch(markConversationRead(conv.id));
            axios.patch(
                `${window.API_BASE_URL}/Conversations/${conv.id}/mark-read`,
                {},
                { headers: { Authorization: `Bearer ${getToken()}` } }
            ).catch(() => {});
        }
    };

    const handleSendMessage = () => {
        if (!messageInput.trim() || !selectedConversation) return;
        dispatch(sendMessageToConversation(selectedConversation.id, messageInput.trim()));
        setMessageInput('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleCloseConversation = async () => {
        if (!selectedConversation) return;
        setClosingConversation(true);
        try {
            await dispatch(closeConversationInAPI(selectedConversation.id));
            setSelectedConversation(prev => ({ ...prev, active: false, Active: false, humanControl: false }));
            setHumanControl(false);
            dispatch(fetchAllConversationsFromAPI(selectedBusinessLine));
        } catch {
            // error ya logueado
        } finally {
            setClosingConversation(false);
        }
    };

    const handleTakeover = async () => {
        if (!selectedConversation) return;
        const newValue = !humanControl;
        setTakingOver(true);
        try {
            await dispatch(takeoverConversationInAPI(selectedConversation.id, newValue));
            setHumanControl(newValue);
            // Actualizar el objeto en la lista también
            dispatch(fetchAllConversationsFromAPI(selectedBusinessLine));
        } catch {
            // error ya logueado
        } finally {
            setTakingOver(false);
        }
    };

    const handleReactivate = async () => {
        if (!selectedConversation) return;
        setReactivatingConversation(true);
        try {
            await dispatch(reactivateConversationInAPI(selectedConversation.id));
            setSelectedConversation(prev => ({ ...prev, active: true, Active: true, humanControl: true, flowCompleted: false, FlowCompleted: false }));
            setHumanControl(true);
            dispatch(fetchAllConversationsFromAPI(selectedBusinessLine));
        } catch (error) {
            if (error?.response?.data?.code === 'WINDOW_EXPIRED') {
                setTemplatePickerOpen(true);
            }
        } finally {
            setReactivatingConversation(false);
        }
    };

    const handleSaveNote = async () => {
        if (!selectedConversation) return;
        setSavingNote(true);
        await dispatch(updateConversationNoteInAPI(selectedConversation.id, agentNote));
        setSavingNote(false);
        setNoteSaved(true);
        setTimeout(() => setNoteSaved(false), 2000);
    };

    const handleSendTemplate = async (templateId) => {
        if (!selectedConversation) return;
        try {
            await dispatch(sendTemplateToConversationInAPI(selectedConversation.id, templateId));
            setTemplatePickerOpen(false);
            // Activar control humano automáticamente tras reabrir con plantilla
            setHumanControl(true);
            dispatch(fetchAllConversationsFromAPI(selectedBusinessLine));
        } catch (error) {
            setErrorMsg(error?.response?.data || 'No se pudo enviar la plantilla.');
            throw error; // que el modal mantenga el estado para reintentar
        }
    };

    const handleStartConversation = useCallback(async (clientId, accountId, message, templateId) => {
        try {
            const result = await dispatch(startConversationInAPI(clientId, accountId, message, templateId));
            setNewConvOpen(false);
            setNewConvPreContact(null);
            // Recargar lista. La selección se hace vía pendingSelectId cuando llega la lista
            // fresca (evita usar allConversationsList stale del closure).
            await dispatch(fetchAllConversationsFromAPI(selectedBusinessLine));
            if (result?.id) setPendingSelectId(result.id);
        } catch (error) {
            setErrorMsg(error?.response?.data || 'No se pudo iniciar la conversación.');
            throw error; // el modal corta su estado de "iniciando" en su finally
        }
    }, [dispatch, selectedBusinessLine]);

    // Selecciona la conversación recién creada una vez que aparece en la lista actualizada.
    useEffect(() => {
        if (pendingSelectId == null) return;
        const conv = allConversationsList?.find(c => c.id === pendingSelectId);
        if (conv) {
            handleSelectConversation(conv);
            setPendingSelectId(null);
        }
    }, [pendingSelectId, allConversationsList]); // eslint-disable-line

    const handleEnqueue = async () => {
        if (!selectedConversation) return;
        try {
            await dispatch(enqueueConversationInAPI(selectedConversation.id));
            setEnqueueSuccess(true);
            setTimeout(() => setEnqueueSuccess(false), 2500);
        } catch {
            // error ya logueado
        }
    };

    if (userBLIds !== null && userBLIds.length === 0) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2, color: 'text.secondary' }}>
                <Typography variant="h4">🏢</Typography>
                <Typography variant="body1" fontWeight={600}>Sin líneas de negocio asignadas</Typography>
                <Typography variant="body2">Contacta al administrador para que te asigne líneas de negocio.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
            {/* ── SIDEBAR ── */}
            <Box sx={{
                width: 300, minWidth: 260, maxWidth: 340,
                borderRight: '1px solid #e8e8e8',
                display: 'flex', flexDirection: 'column',
                bgcolor: '#fff', flexShrink: 0, position: 'relative',
            }}>
                {/* Header sidebar */}
                <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="h6" fontWeight={700}>Conversaciones</Typography>
                        <Tooltip title="Nueva conversación">
                            <Fab
                                size="small"
                                onClick={() => { setNewConvPreContact(null); setNewConvOpen(true); }}
                                sx={{
                                    bgcolor: '#007AFF', color: '#fff',
                                    width: 32, height: 32, minHeight: 32,
                                    boxShadow: '0 2px 8px rgba(0,122,255,0.35)',
                                    '&:hover': { bgcolor: '#005ecb' },
                                }}
                            >
                                <AddCommentIcon sx={{ fontSize: 16 }} />
                            </Fab>
                        </Tooltip>
                    </Box>

                    {/* BL Combobox */}
                    <Box sx={{ mb: 1.5 }}>
                        <Autocomplete
                            size="small"
                            disablePortal
                            disableClearable
                            options={businessLineOptions}
                            value={selectedBusinessLineOption}
                            getOptionLabel={(option) => option?.label || ''}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            onChange={(_, option) => setSelectedBusinessLine(option?.id ?? null)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Línea de negocio"
                                    placeholder="Seleccionar..."
                                />
                            )}
                            sx={{
                                '& .MuiInputBase-root': { fontSize: 13 },
                                '& .MuiInputLabel-root': { fontSize: 13 },
                            }}
                        />
                    </Box>

                    {/* Filtro de estado */}
                    <Box sx={{ mb: 1.25 }}>
                        <Autocomplete
                            size="small"
                            disablePortal
                            disableClearable
                            options={statusFilterOptions}
                            value={selectedStatusFilterOption}
                            getOptionLabel={(option) => option?.label || ''}
                            isOptionEqualToValue={(option, value) => option.key === value?.key}
                            onChange={(_, option) => setStatusFilter(option?.key ?? 'todas')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Estado de conversación"
                                    placeholder="Seleccionar..."
                                />
                            )}
                            sx={{
                                '& .MuiInputBase-root': { fontSize: 13 },
                                '& .MuiInputLabel-root': { fontSize: 13 },
                            }}
                        />
                    </Box>

                    {/* Buscador */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        bgcolor: '#f5f5f5', borderRadius: 2, px: 1.25,
                    }}>
                        <SearchIcon sx={{ color: '#aaa', fontSize: 17, mr: 0.5 }} />
                        <input
                            placeholder="Buscar..."
                            value={filterText}
                            onChange={e => setFilterText(e.target.value)}
                            style={{
                                border: 'none', background: 'transparent', outline: 'none',
                                width: '100%', padding: '7px 0', fontSize: 13,
                            }}
                        />
                    </Box>
                </Box>

                {/* Lista */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {loadingList ? (
                        Array.from({ length: 7 }).map((_, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5 }}>
                                <Skeleton variant="circular" width={42} height={42} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="65%" height={16} />
                                    <Skeleton variant="text" width="45%" height={13} />
                                </Box>
                            </Box>
                        ))
                    ) : filteredConversations.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">Sin resultados</Typography>
                        </Box>
                    ) : (
                        filteredConversations.map(conv => (
                            <ConversationItem
                                key={conv.id}
                                conv={conv}
                                isSelected={selectedConversation?.id === conv.id}
                                onClick={() => handleSelectConversation(conv)}
                            />
                        ))
                    )}
                </Box>
            </Box>

            {/* ── PANEL DERECHO ── */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f7f7f7' }}>
                {!selectedConversation ? (
                    <EmptyState />
                ) : loadingMessages ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <>
                        <ConversationHeader
                            conversation={selectedConversation}
                            humanControl={humanControl}
                            notesOpen={notesOpen}
                            onToggleNotes={() => setNotesOpen(o => !o)}
                            onClose={handleCloseConversation}
                            closing={closingConversation}
                            onTakeover={handleTakeover}
                            takingOver={takingOver}
                            onReactivate={handleReactivate}
                            reactivating={reactivatingConversation}
                            onEnqueue={handleEnqueue}
                            enqueuing={enqueuing}
                            enqueueSuccess={enqueueSuccess}
                        />

                        {/* Barra contextual de estado */}
                        {contextualBarState && (
                            <Box sx={{
                                px: 2,
                                height: 44,
                                bgcolor: contextualBarState.bg,
                                borderBottom: `1px solid ${contextualBarState.border}`,
                                display: 'flex', alignItems: 'center', gap: 1,
                            }}>
                                {contextualBarState.icon}
                                <Typography variant="body2" sx={{ color: contextualBarState.color, fontWeight: 600, fontSize: 12 }}>
                                    {contextualBarState.text}
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                <WhatsAppChat
                                    conversation={selectedConversation}
                                    onClose={() => setSelectedConversation(null)}
                                    height="100%"
                                    maxWidth="100%"
                                    hideHeader={true}
                                />
                            </Box>

                            {/* Input de mensaje */}
                            {(() => {
                                const isActive = selectedConversation?.active;
                                const expired = isActive && isWindowExpired(selectedConversation);
                                const blocked = !isActive;
                                return (
                                    <Box sx={{
                                        display: 'flex', alignItems: 'flex-end', gap: 1,
                                        px: 2, py: 1.25,
                                        borderTop: '1px solid #e8e8e8',
                                        bgcolor: '#fff', flexShrink: 0,
                                    }}>
                                        <TextField
                                            multiline maxRows={4} fullWidth size="small"
                                            placeholder={
                                                blocked ? 'Conversación cerrada — reactiva para continuar'
                                                : expired ? 'Ventana expirada — usa una plantilla para recontactar'
                                                : humanControl ? 'Escribe como agente...'
                                                : 'Escribe un mensaje...'
                                            }
                                            value={messageInput}
                                            onChange={e => setMessageInput(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            disabled={sendingMessage || expired || blocked}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    bgcolor: blocked ? '#f5f5f5' : expired ? '#fafafa' : humanControl ? '#faf5ff' : '#f5f5f5',
                                                    '& fieldset': {
                                                        border: humanControl && !expired && !blocked ? '1px solid #c4b5fd' : 'none',
                                                    },
                                                    '& input::placeholder, & textarea::placeholder': {
                                                        color: blocked ? '#78716c' : expired ? '#9a3412' : '#9e9e9e',
                                                        opacity: 1,
                                                        WebkitTextFillColor: blocked ? '#78716c' : expired ? '#9a3412' : undefined,
                                                    },
                                                },
                                            }}
                                        />
                                        {blocked ? (
                                            <IconButton
                                                disabled
                                                sx={{
                                                    bgcolor: '#e0e0e0', color: '#bbb', flexShrink: 0,
                                                }}
                                            >
                                                <SendIcon fontSize="small" />
                                            </IconButton>
                                        ) : expired ? (
                                            <IconButton
                                                onClick={() => setTemplatePickerOpen(true)}
                                                sx={{
                                                    bgcolor: '#c2410c', color: '#fff', flexShrink: 0,
                                                    '&:hover': { bgcolor: '#9a3412' },
                                                }}
                                            >
                                                <SendAndArchiveIcon fontSize="small" />
                                            </IconButton>
                                        ) : (
                                            <IconButton
                                                onClick={handleSendMessage}
                                                disabled={sendingMessage || !messageInput.trim()}
                                                sx={{
                                                    bgcolor: humanControl ? '#7c3aed' : '#007AFF',
                                                    color: '#fff', flexShrink: 0,
                                                    '&:hover': { bgcolor: humanControl ? '#6d28d9' : '#005ecb' },
                                                    '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#bbb' },
                                                }}
                                            >
                                                {sendingMessage
                                                    ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                                                    : <SendIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        )}
                                    </Box>
                                );
                            })()}

                            {/* Notas internas colapsables */}
                            <Collapse in={notesOpen} timeout={200}>
                                <Box sx={{
                                    borderTop: '1px solid #e8e8e8',
                                    bgcolor: '#fffdf0', px: 2, pt: 1.25, pb: 1.5, flexShrink: 0,
                                }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary"
                                        sx={{ mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Notas internas
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                        <TextField
                                            multiline rows={2} fullWidth size="small"
                                            placeholder="Escribe una nota interna sobre esta conversación..."
                                            value={agentNote}
                                            onChange={e => setAgentNote(e.target.value)}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2, bgcolor: '#fff', fontSize: 13,
                                                },
                                            }}
                                        />
                                        <Button
                                            variant="contained" size="small"
                                            onClick={handleSaveNote}
                                            disabled={savingNote}
                                            sx={{
                                                height: 36, minWidth: 80, flexShrink: 0,
                                                bgcolor: noteSaved ? '#22c55e' : '#007AFF',
                                                '&:hover': { bgcolor: noteSaved ? '#16a34a' : '#005ecb' },
                                                textTransform: 'none', fontWeight: 600, fontSize: 13,
                                                transition: 'background 0.3s',
                                            }}
                                        >
                                            {savingNote
                                                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                                : noteSaved ? '✓ Guardado' : 'Guardar'
                                            }
                                        </Button>
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    </>
                )}
            </Box>

            {/* ── MODAL PLANTILLAS ── */}
            <TemplatePickerModal
                open={templatePickerOpen}
                onClose={() => setTemplatePickerOpen(false)}
                onSend={handleSendTemplate}
                accountId={selectedConversation?.accountId}
            />

            {/* ── MODAL NUEVA CONVERSACIÓN ── */}
            <NewConversationModal
                open={newConvOpen}
                onClose={() => { setNewConvOpen(false); setNewConvPreContact(null); }}
                onStart={handleStartConversation}
                accounts={accounts}
                preContact={newConvPreContact}
                restrictToCartera={userBLIds !== null}
            />

            {/* ── ERRORES (ej. rechazo de plantilla por WhatsApp) ── */}
            <Snackbar
                open={Boolean(errorMsg)}
                autoHideDuration={8000}
                onClose={() => setErrorMsg(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setErrorMsg(null)} sx={{ width: '100%' }}>
                    {errorMsg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ConversacionesPage;
